// stores/user.js
import { defineStore } from 'pinia';
import defaultApi from '@/services/axios';

/**
 * Setzt das App-Theme auf <html>.
 * - my-app-light / my-app-dark: falls du eigene Scopes nutzt
 * - dark:            Tailwind Dark Mode
 * - p-dark:          PrimeVue (Design Tokens) Dark Toggle
 * - colorScheme:     native Controls (Scrollbars, Inputs)
 * Optional (auskommentiert): data-p-theme für Theme-Switch via Tokens
 */
function applyTheme(mode) {
  const root = document.documentElement;
  const isDark = mode === 'dark';

  // Eigene Klassen (falls im CSS genutzt)
  root.classList.toggle('my-app-dark',  isDark);
  root.classList.toggle('my-app-light', !isDark);

  // Tailwind Dark Mode
  root.classList.toggle('dark', isDark);

  // PrimeVue: Dark Toggle (Design Tokens)
  root.classList.toggle('p-dark', isDark);

  // Optional: falls ihr per data-Attribut arbeitet (z. B. Aura/Lara Token Theme)
  // root.setAttribute('data-p-theme', isDark ? 'aura-dark' : 'aura');

  // Native Form Controls (Scrollbars etc.)
  root.style.colorScheme = isDark ? 'dark' : 'light';
}

export const useUserStore = defineStore('user', {
  state: () => ({
    info: null, // { id, first_name, last_name, email, avatar_url, role, permissions:[], isAdmin: bool, ... }
    settings: {
      preferredColorMode: 'light', // 'light' | 'dark'
    },
    isLoading: false,
    _loaded: false, // verhindert doppeltes Laden
  }),

  getters: {
    // Admin wenn Flag true ODER 'admin.access' in permissions
    isAdmin: (s) =>
      !!s.info?.isAdmin || (s.info?.permissions?.includes('admin.access') || false),

    // true wenn die Company des Users is_partner = true hat (aus view_user_details)
    isPartner: (s) => s.info?.company_is_partner === true,

    // can('perm') => true wenn explizit erlaubt ODER Admin
    can: (s) => (perm) => {
      if (s.info?.isAdmin) return true;
      const perms = s.info?.permissions || [];
      return perms.includes(perm) || perms.includes('admin.access');
    },

    // aktueller Farbmodus
    colorMode: (s) => s.settings?.preferredColorMode || 'light',
  },

  actions: {
    /**
     * Lädt /users/me
     */
    async fetchMe(api = defaultApi) {
      this.isLoading = true;
      try {
        const { data } = await api.get('/users/me');
        this.info = data;
      } catch (e) {
        // Fallback: User bleibt null
        console.error('[user.store] fetchMe failed:', e);
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Lädt /users/me/settings und wendet das Theme an.
     */
    async fetchSettings(api = defaultApi) {
      try {
        const { data } = await api.get('/users/me/settings');
        this.settings = { ...this.settings, ...data };
      } catch (e) {
        console.warn('[user.store] fetchSettings failed, using existing settings:', e);
      } finally {
        // Theme immer anwenden – auch wenn das Laden fehlschlug (nutzt aktuellen Store-Wert)
        applyTheme(this.settings.preferredColorMode);
      }
    },

    /**
     * Setzt + speichert das Theme (light/dark).
     * UI-Effekt passiert sofort; Server-Speichern best effort.
     */
    async setColorMode(mode, api = defaultApi) {
      this.settings.preferredColorMode = mode;
      applyTheme(mode); // sofort sichtbar

      try {
        await api.put('/users/me/settings', { preferredColorMode: mode });
      } catch (e) {
        console.error('[user.store] setColorMode save failed:', e);
        // kein Re-throw – UI soll dunkel/hell bleiben
      }
    },

    /**
     * Einmalig alles laden (User + Settings) & Theme anwenden.
     * Falls Settings-Request scheitert, wird zumindest der aktuelle Store-Wert angewendet.
     */
    async init(api = defaultApi) {
      if (this._loaded) {
        // trotzdem sicherstellen, dass das Theme korrekt ist (z. B. nach Reload)
        applyTheme(this.settings.preferredColorMode);
        return;
      }

      await this.fetchMe(api);
      await this.fetchSettings(api); // ruft applyTheme() im finally auf
      this._loaded = true;
    },
  },
});